import { cloneDeep } from 'lodash-es';
import { validateWhiteSpace } from 'platform/forms/validations';
import environment from 'platform/utilities/environment';
import VaTextInputField from '../web-component-fields/VaTextInputField';
import VaSelectField from '../web-component-fields/VaSelectField';

export function validateEmpty(errors, pageData) {
  const { first, last } = pageData;
  validateWhiteSpace(errors.first, first);
  validateWhiteSpace(errors.last, last);
}

// Some back end services such as benefits intake api only accept
// a-z, A-Z, hyphen, and spaces, but this is a minimal set of
// symbols to validate, and we let our local backend handle the rest,
// for example what to do with a name like José Ramírez
export function validateNameSymbols(errors, value, uiSchema, schema, messages) {
  const invalidCharsPattern = /[~!@#$%^&*+=[\]{}()<>;:"`\\/_|]/g;
  const matches = value.match(invalidCharsPattern);

  if (matches) {
    const uniqueInvalidChars = [...new Set(matches)].join(', ');
    const staticText =
      messages?.symbols ||
      'You entered a character we can’t accept. Try removing';
    errors.addError(`${staticText} ${uniqueInvalidChars}`);
  }
}

const FULL_NAME_KEYS = ['first', 'middle', 'last', 'suffix'];

/**
 * @typedef {UIOptions & {
 *   title?: UISchemaOptions['ui:title'],
 *   required?: UISchemaOptions['ui:required'],
 *   validations?: UISchemaOptions['ui:validations'],
 *   errorMessages?: UISchemaOptions['ui:errorMessages'],
 * }} NameFieldUIOptions
 */

/**
 * @typedef {NameFieldUIOptions & { initial?: boolean }} MiddleNameFieldUIOptions
 */

/**
 * @typedef {{
 *   first?: NameFieldUIOptions,
 *   middle?: MiddleNameFieldUIOptions,
 *   last?: NameFieldUIOptions,
 *   suffix?: NameFieldUIOptions,
 * }} FullNameUIOptions
 */

function fieldValidations(validations = []) {
  return [validateNameSymbols, ...validations];
}

function validateMiddleInitial(errors, value) {
  if (value && value.length > 1) {
    errors.addError('Enter only the first letter of the middle name');
  }
}

/**
 * uiSchema for full name `first` field - "First or given name"
 *
 * ```js
 * first: firstNameUI()
 * first: firstNameUI('Your first name')
 * first: firstNameUI({ title: 'Your first name', errorMessages: { required: 'Enter your first name' } })
 * ```
 * @param {NameFieldUIOptions} [options]
 * @returns {UISchemaOptions} uiSchema
 */
const firstNameUI = options => {
  const { title, required, validations, errorMessages, ...uiOptions } =
    typeof options === 'object' ? options : { title: options };
  return {
    'ui:title': title || 'First or given name',
    'ui:autocomplete': 'given-name',
    'ui:webComponentField': VaTextInputField,
    'ui:required': required,
    'ui:validations': fieldValidations(validations),
    'ui:errorMessages': {
      required: 'Enter a first or given name',
      ...errorMessages,
    },
    'ui:options': { ...uiOptions },
  };
};

/**
 * uiSchema for full name `middle` name field - "Middle name"
 *
 * ```js
 * middle: middleNameUI()
 * middle: middleNameUI('Your middle name')
 * middle: middleNameUI({ initial: true })
 * middle: middleNameUI({ title: 'Your middle name', errorMessages: { required: 'Enter your middle name' } })
 * ```
 * @param {MiddleNameFieldUIOptions} [options]
 * @returns {UISchemaOptions} uiSchema
 */
const middleNameUI = options => {
  const { title, initial, required, validations, errorMessages, ...uiOptions } =
    typeof options === 'object' ? options : { title: options };
  return {
    'ui:title': title || (initial ? 'Middle initial' : 'Middle name'),
    'ui:autocomplete': 'additional-name',
    'ui:webComponentField': VaTextInputField,
    'ui:required': required,
    'ui:validations': fieldValidations(
      initial ? [validateMiddleInitial, ...(validations || [])] : validations,
    ),
    'ui:errorMessages': errorMessages,
    'ui:options': { ...(initial && { maxlength: 1 }), ...uiOptions },
  };
};

/**
 * uiSchema for full name `last` field - "Last or family name"
 *
 * ```js
 * last: lastNameUI()
 * last: lastNameUI('Your last name')
 * last: lastNameUI({ title: 'Your last name', errorMessages: { required: 'Enter your last name' } })
 * ```
 * @param {NameFieldUIOptions} [options]
 * @returns {UISchemaOptions} uiSchema
 */
const lastNameUI = options => {
  const { title, required, validations, errorMessages, ...uiOptions } =
    typeof options === 'object' ? options : { title: options };
  return {
    'ui:title': title || 'Last or family name',
    'ui:autocomplete': 'family-name',
    'ui:webComponentField': VaTextInputField,
    'ui:required': required,
    'ui:validations': fieldValidations(validations),
    'ui:errorMessages': {
      required: 'Enter a last or family name',
      ...errorMessages,
    },
    'ui:options': { ...uiOptions },
  };
};

/**
 * uiSchema for full name `suffix` field - "Suffix"
 *
 * ```js
 * suffix: suffixUI()
 * suffix: suffixUI('Your suffix')
 * suffix: suffixUI({ title: 'Your suffix', errorMessages: { required: 'Enter your suffix' } })
 * ```
 * @param {NameFieldUIOptions} [options]
 * @returns {UISchemaOptions} uiSchema
 */
const suffixUI = options => {
  const { title, required, errorMessages, ...uiOptions } =
    typeof options === 'object' ? options : { title: options };
  return {
    'ui:title': title || 'Suffix',
    'ui:autocomplete': 'honorific-suffix',
    'ui:webComponentField': VaSelectField,
    'ui:required': required,
    'ui:errorMessages': errorMessages,
    'ui:options': {
      widgetClassNames: 'form-select-medium',
      ...uiOptions,
    },
  };
};

/**
 * Builder for full name uiSchemas. The first argument is either a per-field
 * options object (keys: first, middle, last, suffix) or a `formatTitle`
 * function. `uiOptions` applies shared `ui:options` to every field.
 *
 * @param {FullNameUIOptions | ((title: string) => string)} [formatTitleOrOptions]
 * @param {UIOptions} [uiOptions] optional 'ui:options' to apply to every field
 * @param {('first' | 'middle' | 'last' | 'suffix')[]} [omit]
 * @returns {UISchemaOptions} uiSchema
 */
const buildFullNameUI = (formatTitleOrOptions, uiOptions = {}, omit = []) => {
  const formatTitle =
    typeof formatTitleOrOptions === 'function' ? formatTitleOrOptions : null;
  const fieldOptions = formatTitle ? {} : formatTitleOrOptions || {};

  const unknownKeys = Object.keys(fieldOptions).filter(
    key => !FULL_NAME_KEYS.includes(key),
  );
  if (unknownKeys.length && !environment.isProduction()) {
    throw new Error(
      `fullNameUI: unrecognized option(s) "${unknownKeys.join(
        '", "',
      )}". Configure each field under first, middle, last, or suffix.`,
    );
  }

  const optionsFor = field => ({ ...uiOptions, ...fieldOptions[field] });

  const withFormatTitle = field =>
    formatTitle
      ? { ...field, 'ui:title': formatTitle(field['ui:title'].toLowerCase()) }
      : field;

  return {
    'ui:validations': [validateEmpty],
    ...(!omit.includes('first') && {
      first: withFormatTitle(firstNameUI(optionsFor('first'))),
    }),
    ...(!omit.includes('middle') && {
      middle: withFormatTitle(middleNameUI(optionsFor('middle'))),
    }),
    ...(!omit.includes('last') && {
      last: withFormatTitle(lastNameUI(optionsFor('last'))),
    }),
    ...(!omit.includes('suffix') && {
      suffix: withFormatTitle(suffixUI(optionsFor('suffix'))),
    }),
  };
};

/**
 * uiSchema for `first`, `middle`, and `last name`
 *
 * ```js
 * fullName: fullNameNoSuffixUI()
 * fullName: fullNameNoSuffixUI({ first: { hint: 'Legal first name' } })
 * fullName: fullNameNoSuffixUI({ middle: { initial: true } })
 * fullName: fullNameNoSuffixUI((title) => `Your ${title}`)
 * ```
 * @param {FullNameUIOptions | ((title: string) => string)} [formatTitleOrOptions] per-field options, or a formatTitle function
 * @param {UIOptions} [uiOptions] optional 'ui:options' to apply to every field
 * @returns {UISchemaOptions} uiSchema
 */
const fullNameNoSuffixUI = (formatTitleOrOptions, uiOptions = {}) =>
  buildFullNameUI(formatTitleOrOptions, uiOptions, ['suffix']);

/**
 * uiSchema for `first` and `last name` only
 *
 * ```js
 * fullName: firstNameLastNameNoSuffixUI()
 * fullName: firstNameLastNameNoSuffixUI({ first: { hint: 'Legal first name' } })
 * fullName: firstNameLastNameNoSuffixUI((title) => `Your ${title}`)
 * ```
 * @param {FullNameUIOptions | ((title: string) => string)} [formatTitleOrOptions] per-field options, or a formatTitle function
 * @param {UIOptions} [uiOptions] optional 'ui:options' to apply to every field
 * @returns {UISchemaOptions} uiSchema
 */
const firstNameLastNameNoSuffixUI = (formatTitleOrOptions, uiOptions = {}) =>
  buildFullNameUI(formatTitleOrOptions, uiOptions, ['middle', 'suffix']);

/**
 * uiSchema for `first`, `middle`, `last name`, and `suffix`
 *
 * ```js
 * fullName: fullNameUI()
 * fullName: fullNameUI({ first: { hint: 'Legal first name' } })
 * fullName: fullNameUI({ middle: { initial: true } })
 * fullName: fullNameUI((title) => `Your ${title}`)
 * ```
 * @param {FullNameUIOptions | ((title: string) => string)} [formatTitleOrOptions] per-field options, or a formatTitle function
 * @param {UIOptions} [uiOptions] optional 'ui:options' to apply to every field
 * @returns {UISchemaOptions} uiSchema
 */
const fullNameUI = (formatTitleOrOptions, uiOptions = {}) =>
  buildFullNameUI(formatTitleOrOptions, uiOptions);

/**
 * uiSchema for `first`, `last name`, and `suffix`
 *
 * ```js
 * fullName: firstNameLastNameUI()
 * fullName: firstNameLastNameUI({ suffix: { title: 'Suffix (optional)' } })
 * fullName: firstNameLastNameUI((title) => `Your ${title}`)
 * ```
 * @param {FullNameUIOptions | ((title: string) => string)} [formatTitleOrOptions] per-field options, or a formatTitle function
 * @param {UIOptions} [uiOptions] optional 'ui:options' to apply to every field
 * @returns {UISchemaOptions} uiSchema
 */
const firstNameLastNameUI = (formatTitleOrOptions, uiOptions = {}) =>
  buildFullNameUI(formatTitleOrOptions, uiOptions, ['middle']);

/**
 * schema for `firstNameUI`
 */
const firstNameSchema = {
  type: 'string',
  minLength: 1,
  maxLength: 30,
};

/**
 * schema for `middleNameUI`
 */
const middleNameSchema = {
  type: 'string',
  maxLength: 30,
};

/**
 * schema for `lastNameUI`
 */
const lastNameSchema = {
  type: 'string',
  minLength: 1,
  maxLength: 30,
};

/**
 * schema for `suffixUI`
 */
const suffixSchema = {
  type: 'string',
  enum: ['Jr.', 'Sr.', 'II', 'III', 'IV'],
};

/**
 * schema for `fullNameUI`
 */
const fullNameSchema = {
  type: 'object',
  required: ['first', 'last'],
  properties: {
    first: cloneDeep(firstNameSchema),
    middle: cloneDeep(middleNameSchema),
    last: cloneDeep(lastNameSchema),
    suffix: cloneDeep(suffixSchema),
  },
};

/**
 * schema for `firstNameLastNameUI`
 */
const firstNameLastNameSchema = {
  type: 'object',
  required: ['first', 'last'],
  properties: {
    first: cloneDeep(firstNameSchema),
    last: cloneDeep(lastNameSchema),
    suffix: cloneDeep(suffixSchema),
  },
};

/**
 * schema for `fullNameNoSuffixUI`
 */
const fullNameNoSuffixSchema = {
  type: 'object',
  required: ['first', 'last'],
  properties: {
    first: cloneDeep(firstNameSchema),
    middle: cloneDeep(middleNameSchema),
    last: cloneDeep(lastNameSchema),
  },
};

/**
 * schema for `firstNameLastNameNoSuffixUI`
 */
const firstNameLastNameNoSuffixSchema = {
  type: 'object',
  required: ['first', 'last'],
  properties: {
    first: cloneDeep(firstNameSchema),
    last: cloneDeep(lastNameSchema),
  },
};

export {
  fullNameNoSuffixUI,
  firstNameLastNameNoSuffixUI,
  fullNameUI,
  firstNameLastNameUI,
  fullNameSchema,
  firstNameLastNameSchema,
  fullNameNoSuffixSchema,
  firstNameLastNameNoSuffixSchema,
};
